import { useState, useCallback, useEffect, useRef } from 'react';
import { useDocumentStore } from '../../stores/documentStore';
import { useUIStore } from '../../stores/uiStore';
import { PDFManipulator } from '../../core/PDFManipulator';
import { pdfEngine } from '../../core/PDFEngine';
import { uint8ArrayToArrayBuffer } from '../../utils/fileUtils';
import './Dialogs.css';

interface PageOrganizerProps {
  onClose: () => void;
}

interface PageItem {
  id: string;
  originalPageNumber: number;
  rotation: number; // 0, 90, 180, 270
  isBlank?: boolean;
}

export function PageOrganizer({ onClose }: PageOrganizerProps) {
  const { documents, activeDocumentId, totalPages } = useDocumentStore();
  const activeDoc = documents.find((d) => d.id === activeDocumentId);
  const [pages, setPages] = useState<PageItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const thumbnailRefs = useRef<Map<string, HTMLCanvasElement>>(new Map());

  // Initialize pages list
  useEffect(() => {
    const initialPages: PageItem[] = Array.from({ length: totalPages }, (_, i) => ({
      id: `page-${i + 1}-${crypto.randomUUID()}`,
      originalPageNumber: i + 1,
      rotation: 0,
    }));
    setPages(initialPages);
  }, [totalPages]);

  // Render thumbnails
  useEffect(() => {
    pages.forEach((page) => {
      if (page.isBlank) return;
      const canvas = thumbnailRefs.current.get(page.id);
      if (canvas) {
        pdfEngine.renderPage(page.originalPageNumber, {
          scale: 0.25,
          rotation: page.rotation,
          canvas,
        }).catch(console.error);
      }
    });
  }, [pages]);

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleDeleteSelected = useCallback(() => {
    if (pages.length <= selectedIds.size) {
      useUIStore.getState().addToast({ type: 'warning', message: 'Cannot delete all pages' });
      return;
    }
    setPages((prev) => prev.filter((p) => !selectedIds.has(p.id)));
    setSelectedIds(new Set());
  }, [pages.length, selectedIds]);

  const handleRotateSelected = useCallback((angle: number) => {
    setPages((prev) =>
      prev.map((p) => {
        if (selectedIds.has(p.id)) {
          return { ...p, rotation: (p.rotation + angle + 360) % 360 };
        }
        return p;
      })
    );
  }, [selectedIds]);

  const handleAddBlankPage = useCallback(() => {
    const newBlank: PageItem = {
      id: `blank-${crypto.randomUUID()}`,
      originalPageNumber: -1,
      rotation: 0,
      isBlank: true,
    };

    if (selectedIds.size > 0) {
      const selectedArr = Array.from(selectedIds);
      const lastSelectedId = selectedArr[selectedArr.length - 1];
      const idx = pages.findIndex((p) => p.id === lastSelectedId);
      const newPages = [...pages];
      newPages.splice(idx + 1, 0, newBlank);
      setPages(newPages);
    } else {
      setPages((prev) => [...prev, newBlank]);
    }
  }, [pages, selectedIds]);

  const handleMoveLeft = useCallback(() => {
    if (selectedIds.size !== 1) return;
    const id = Array.from(selectedIds)[0];
    const idx = pages.findIndex((p) => p.id === id);
    if (idx <= 0) return;

    const newPages = [...pages];
    [newPages[idx - 1], newPages[idx]] = [newPages[idx], newPages[idx - 1]];
    setPages(newPages);
  }, [pages, selectedIds]);

  const handleMoveRight = useCallback(() => {
    if (selectedIds.size !== 1) return;
    const id = Array.from(selectedIds)[0];
    const idx = pages.findIndex((p) => p.id === id);
    if (idx < 0 || idx >= pages.length - 1) return;

    const newPages = [...pages];
    [newPages[idx], newPages[idx + 1]] = [newPages[idx + 1], newPages[idx]];
    setPages(newPages);
  }, [pages, selectedIds]);

  const handleApply = useCallback(async () => {
    if (!activeDoc?.arrayBuffer) return;
    setIsProcessing(true);

    try {
      useDocumentStore.getState().pushDocHistory(activeDoc.id, 'Organize Pages');
      let currentBuffer = activeDoc.arrayBuffer;

      // 1. Reorder existing non-blank pages or insert blanks
      const pageOrder = pages.map((p) => p.originalPageNumber);

      // If we have blank pages or reordered pages:
      const hasBlanks = pages.some((p) => p.isBlank);
      
      if (!hasBlanks) {
        // Simple reorder
        const reordered = await PDFManipulator.reorderPages(currentBuffer, pageOrder);
        currentBuffer = uint8ArrayToArrayBuffer(reordered);
      } else {
        // Reconstruct document page by page
        const newPagesOrder: number[] = [];
        const blankIndices: number[] = [];
        pages.forEach((p, idx) => {
          if (p.isBlank) {
            blankIndices.push(idx);
          } else {
            newPagesOrder.push(p.originalPageNumber);
          }
        });

        let tempResult = await PDFManipulator.extractPages(currentBuffer, newPagesOrder);
        // Insert blank pages at their target indices
        for (const blankIdx of blankIndices) {
          tempResult = await PDFManipulator.addBlankPage(uint8ArrayToArrayBuffer(tempResult), blankIdx);
        }
        currentBuffer = uint8ArrayToArrayBuffer(tempResult);
      }

      // 2. Apply rotations
      for (let i = 0; i < pages.length; i++) {
        if (pages[i].rotation !== 0) {
          const rotated = await PDFManipulator.rotatePages(currentBuffer, [i + 1], pages[i].rotation);
          currentBuffer = uint8ArrayToArrayBuffer(rotated);
        }
      }

      // Reload document into viewer
      await pdfEngine.loadDocument(currentBuffer.slice(0));

      useDocumentStore.getState().updateDocument(activeDoc.id, {
        arrayBuffer: currentBuffer,
        pageCount: pages.length,
        lastModified: Date.now(),
      });
      useDocumentStore.getState().setTotalPages(pages.length);
      useDocumentStore.getState().setCurrentPage(1);

      useUIStore.getState().addToast({ type: 'success', message: 'Page changes applied successfully!' });
      onClose();
    } catch (err) {
      console.error('Reorganize pages failed:', err);
      useUIStore.getState().addToast({ type: 'error', message: 'Failed to apply page changes' });
    } finally {
      setIsProcessing(false);
    }
  }, [activeDoc, pages, onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 720, maxHeight: '85vh' }}>
        <div className="modal-header">
          <h3>📄 Organize & Rearrange Pages</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>×</button>
        </div>

        {/* Toolbar Controls */}
        <div
          style={{
            padding: '10px 16px',
            backgroundColor: 'var(--color-bg-tertiary)',
            borderBottom: '1px solid var(--color-surface-border)',
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <button className="btn btn-secondary btn-sm" onClick={handleAddBlankPage}>
            ➕ Add Blank Page
          </button>
          <div className="divider" style={{ height: 20 }} />
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => handleRotateSelected(-90)}
            disabled={selectedIds.size === 0}
          >
            ↺ Rotate Left
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => handleRotateSelected(90)}
            disabled={selectedIds.size === 0}
          >
            ↻ Rotate Right
          </button>
          <div className="divider" style={{ height: 20 }} />
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleMoveLeft}
            disabled={selectedIds.size !== 1}
          >
            ◀ Move Left
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleMoveRight}
            disabled={selectedIds.size !== 1}
          >
            Move Right ▶
          </button>
          <div className="divider" style={{ height: 20 }} />
          <button
            className="btn btn-danger btn-sm"
            onClick={handleDeleteSelected}
            disabled={selectedIds.size === 0}
          >
            🗑️ Delete ({selectedIds.size})
          </button>
        </div>

        {/* Page Grid */}
        <div className="modal-body" style={{ maxHeight: '55vh', overflowY: 'auto', padding: '16px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
              gap: '12px',
            }}
          >
            {pages.map((page, index) => (
              <div
                key={page.id}
                onClick={() => toggleSelection(page.id)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '8px',
                  borderRadius: '8px',
                  border: selectedIds.has(page.id)
                    ? '2px solid var(--color-accent)'
                    : '1px solid var(--color-surface-border)',
                  backgroundColor: selectedIds.has(page.id)
                    ? 'var(--color-accent-subtle)'
                    : 'var(--color-bg-secondary)',
                  cursor: 'pointer',
                  position: 'relative',
                  userSelect: 'none',
                }}
              >
                <div
                  style={{
                    width: '90px',
                    height: '120px',
                    backgroundColor: page.isBlank ? '#f8fafc' : 'white',
                    border: '1px solid #cbd5e1',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}
                >
                  {page.isBlank ? (
                    <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 'bold' }}>Blank Page</span>
                  ) : (
                    <canvas
                      ref={(el) => {
                        if (el) thumbnailRefs.current.set(page.id, el);
                      }}
                      style={{ maxWidth: '100%', maxHeight: '100%', display: 'block' }}
                    />
                  )}
                </div>

                <div
                  style={{
                    marginTop: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: 'var(--color-text-primary)',
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'center',
                  }}
                >
                  <span>Page {index + 1}</span>
                  {page.rotation > 0 && (
                    <span style={{ fontSize: '9px', color: 'var(--color-accent)' }}>({page.rotation}°)</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <span className="dialog-info">{pages.length} pages total</span>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleApply} disabled={isProcessing}>
            {isProcessing ? '⏳ Applying...' : 'Apply Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
